/**
 * nova-eyes.tsx — all 19 canonical eye shapes, rendered on the face screen.
 * Element class names (eye-left, thinking-block-*, load-dot-*, susp-pupil)
 * match nova.css so the canonical keyframe animations apply unchanged.
 */
import * as React from "react";

import {
  caretPath,
  downArcPath,
  heartPath,
  NovaLayout,
  softTrianglePath,
  spiralPath,
  star4Path,
  teardropPath,
  upArcPath,
} from "./nova-geometry";
import { NOVA_COLORS } from "./nova-palette";
import type { NovaEyeBrightness, NovaEyeLook, NovaEyeShape } from "./nova-types";

export interface NovaEyesProps {
  layout: NovaLayout;
  shape: NovaEyeShape;
  color: string;
  bright?: NovaEyeBrightness;
  asym?: boolean;
  look?: NovaEyeLook;
  /** Sleepy dims eyes to 55%. */
  dim?: number;
  /**
   * NOVA-LOAD-1 — per-block fills for `thinkBlocks`, indexed by build
   * order (0 = bottom-left … 5 = apex). Optional on purpose: when it is
   * absent every block keeps the single flat `color` fill, which is what
   * the `thinking` expression (and every other surface) still wants.
   */
  blockColors?: readonly string[];
}

interface EyeGProps {
  cx: number;
  cy: number;
  children: React.ReactNode;
}

/** Wrapper <g> with the class + transform-origin nova.css keyframes expect. */
function Eye({ cx, cy, side, children }: EyeGProps & { side: "left" | "right" }) {
  return (
    <g className={`eye-${side}`} style={{ transformOrigin: `${cx.toFixed(2)}px ${cy.toFixed(2)}px` }}>
      {children}
    </g>
  );
}

/** Triangle-of-blocks eye (Thinking) — blocks pulse in build order via CSS. */
function ThinkBlocks({
  cx,
  cy,
  w,
  h,
  fill,
  side,
  blockColors,
}: {
  cx: number;
  cy: number;
  w: number;
  h: number;
  fill: string;
  side: "l" | "r";
  blockColors?: readonly string[];
}) {
  const left = cx - w / 2;
  const top = cy - h / 2;
  const gap = 1.2;
  const blockW = w / 3 - gap;
  const blockH = h / 3 - gap;
  const rowY = (i: number) => top + (i * h) / 3 + gap / 2;
  const blocks: Array<{ x: number; y: number; order: number }> = [
    // bottom row: 3 blocks (built first)
    ...[0, 1, 2].map((c) => ({ x: left + (c * w) / 3 + gap / 2, y: rowY(2), order: c })),
    // middle row: 2 blocks
    ...[0, 1].map((c) => ({ x: left + ((c + 0.5) * w) / 3 + gap / 2, y: rowY(1), order: 3 + c })),
    // top point
    { x: left + w / 3 + gap / 2, y: rowY(0), order: 5 },
  ];
  return (
    <g className={`eye-${side === "l" ? "left" : "right"}`}>
      {blocks.map((b) => (
        <rect
          key={b.order}
          className={`thinking-block thinking-block-${side}-${b.order}`}
          x={b.x.toFixed(2)}
          y={b.y.toFixed(2)}
          width={blockW.toFixed(2)}
          height={blockH.toFixed(2)}
          rx={2}
          ry={2}
          fill={blockColors?.[b.order] ?? fill}
          style={{ transformOrigin: `${(b.x + blockW / 2).toFixed(2)}px ${(b.y + blockH / 2).toFixed(2)}px` }}
        />
      ))}
    </g>
  );
}

export function NovaEyes({ layout, shape, color, bright = "soft", asym = false, look = "center", dim = 1, blockColors }: NovaEyesProps) {
  const { screenCx, screenCy, eyeW, eyeH } = layout;
  const gapHalf = layout.eyeGapHalf;
  const lx = screenCx - gapHalf;
  const rx = screenCx + gapHalf;
  const cy = screenCy;

  switch (shape) {
    case "thinkBlocks":
      return (
        <>
          <ThinkBlocks cx={lx} cy={cy} w={eyeW} h={eyeH} fill={color} side="l" blockColors={blockColors} />
          <ThinkBlocks cx={rx} cy={cy} w={eyeW} h={eyeH} fill={color} side="r" blockColors={blockColors} />
        </>
      );

    case "curveSmile": {
      const arcW = eyeW * 1.05;
      const arcH = eyeH * 0.55;
      const stroke = Math.max(8, eyeH * 0.18);
      const arc = (cx: number) => upArcPath(cx, cy + 4, arcW, arcH);
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>
            <path d={arc(lx)} stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none" opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={arc(rx)} stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none" opacity={dim} />
          </Eye>
        </>
      );
    }

    case "flat": {
      const barW = eyeW;
      const barH = Math.max(8, eyeH * 0.14);
      const bar = (cx: number) => (
        <rect x={cx - barW / 2} y={cy - barH / 2} width={barW} height={barH} rx={barH / 2} fill={color} opacity={0.7 * dim} />
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{bar(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{bar(rx)}</Eye>
        </>
      );
    }

    case "teardrop":
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>
            <path d={teardropPath(lx, cy, eyeW, eyeH)} fill={color} opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={teardropPath(rx, cy, eyeW, eyeH)} fill={color} opacity={dim} />
          </Eye>
        </>
      );

    case "shocked": {
      const erx = eyeW * 0.58;
      const ery = eyeH * 0.46;
      const lookDx = look === "right" ? 6 : look === "left" ? -6 : 0;
      const pr = Math.max(5, eyeW * 0.14);
      const eye = (cx: number) => (
        <>
          <ellipse cx={cx} cy={cy} rx={erx.toFixed(2)} ry={ery.toFixed(2)} fill={color} opacity={dim} />
          <circle cx={cx + lookDx} cy={cy} r={pr.toFixed(2)} fill={NOVA_COLORS.deep} />
        </>
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{eye(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{eye(rx)}</Eye>
        </>
      );
    }

    case "triFurrow": {
      // Angry V — compressed triangles tilted sharply inward (±25°).
      const h = eyeH * 0.85;
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>
            <path d={softTrianglePath(lx, cy, eyeW, h, "up", 25)} fill={color} opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={softTrianglePath(rx, cy, eyeW, h, "up", -25)} fill={color} opacity={dim} />
          </Eye>
        </>
      );
    }

    case "hearts": {
      const hw = eyeW * 1.05;
      const hh = eyeH * 0.9;
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>
            <path d={heartPath(lx, cy, hw, hh)} fill={color} opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={heartPath(rx, cy, hw, hh)} fill={color} opacity={dim} />
          </Eye>
        </>
      );
    }

    case "laugh": {
      const cw = eyeW * 0.95;
      const ch = eyeH * 0.5;
      const stroke = Math.max(9, eyeH * 0.2);
      const caret = (cx: number) => (
        <path d={caretPath(cx, cy, cw, ch)} stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" fill="none" opacity={dim} />
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{caret(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{caret(rx)}</Eye>
        </>
      );
    }

    case "dizzy": {
      const r = eyeH * 0.42;
      const spiral = (cx: number) => (
        <path d={spiralPath(cx, cy, r)} stroke={color} strokeWidth={7} strokeLinecap="round" fill="none" opacity={dim} />
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{spiral(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{spiral(rx)}</Eye>
        </>
      );
    }

    case "wink": {
      const arcW = eyeW * 1.05;
      const arcH = eyeH * 0.5;
      const stroke = Math.max(8, eyeH * 0.18);
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>
            <path d={softTrianglePath(lx, cy, eyeW, eyeH, "up", -7)} fill={color} opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={upArcPath(rx, cy, arcW, arcH)} stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none" opacity={dim} />
          </Eye>
        </>
      );
    }

    case "stars": {
      const r = eyeH * 0.52;
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>
            <path d={star4Path(lx, cy, r)} fill={color} opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={star4Path(rx, cy, r)} fill={color} opacity={dim} />
          </Eye>
        </>
      );
    }

    case "smug": {
      // Half-lidded: triangle with a screen-colored lid covering the top.
      const h = eyeH * 0.5;
      const lid = (cx: number, tilt: number) => (
        <>
          <path d={softTrianglePath(cx, cy + h * 0.2, eyeW, h, "up", tilt)} fill={color} opacity={dim} />
          <rect x={cx - eyeW / 2 - 4} y={(cy - h * 0.62).toFixed(1)} width={eyeW + 8} height={h * 0.42} rx={4} fill={NOVA_COLORS.screen} />
        </>
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{lid(lx, -10)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{lid(rx, 10)}</Eye>
        </>
      );
    }

    case "pleading": {
      const r = eyeH * 0.46;
      const puppy = (cx: number) => (
        <>
          <circle cx={cx} cy={cy} r={r} fill={color} opacity={dim} />
          <circle cx={cx - r * 0.32} cy={cy - r * 0.35} r={r * 0.28} fill="#FFFFFF" opacity={0.85} />
          <circle cx={cx + r * 0.3} cy={cy + r * 0.22} r={r * 0.14} fill="#FFFFFF" opacity={0.6} />
        </>
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{puppy(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{puppy(rx)}</Eye>
        </>
      );
    }

    case "suspicious": {
      const w = eyeW * 1.05;
      const h = eyeH * 0.26;
      const slit = (cx: number) => (
        <>
          <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx={h / 2} fill={color} opacity={dim} />
          <circle className="susp-pupil" cx={cx + w * 0.22} cy={cy} r={h * 0.4} fill={NOVA_COLORS.screen} />
        </>
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{slit(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{slit(rx)}</Eye>
        </>
      );
    }

    case "loading": {
      const dr = eyeH * 0.12;
      const dotGap = dr * 3;
      const dots = (cx: number, side: "l" | "r") => (
        <g className={`eye-${side === "l" ? "left" : "right"}`}>
          {[-1, 0, 1].map((i, idx) => (
            <circle
              key={idx}
              className={`load-dot load-dot-${side}-${idx}`}
              cx={cx + i * dotGap}
              cy={cy}
              r={dr}
              fill={color}
              style={{ transformOrigin: `${(cx + i * dotGap).toFixed(1)}px ${cy.toFixed(1)}px` }}
            />
          ))}
        </g>
      );
      return (
        <>
          {dots(lx, "l")}
          {dots(rx, "r")}
        </>
      );
    }

    case "crying": {
      const arcW = eyeW * 1.05;
      const arcH = eyeH * 0.5;
      const stroke = Math.max(8, eyeH * 0.18);
      const arc = (cx: number) => (
        <path d={downArcPath(cx, cy, arcW, arcH)} stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none" opacity={dim} />
      );
      return (
        <>
          <Eye side="left" cx={lx} cy={cy}>{arc(lx)}</Eye>
          <Eye side="right" cx={rx} cy={cy}>{arc(rx)}</Eye>
        </>
      );
    }

    // triUp / triDown / triUpForward
    default: {
      const orient = shape === "triDown" ? "down" : "up";
      let leftTilt = 0;
      let rightTilt = 0;
      if (asym) {
        leftTilt = 14; // curious: tilt inward toward each other
        rightTilt = -14;
      } else if (shape !== "triDown") {
        leftTilt = -7; // default subtle outward tilt
        rightTilt = 7;
      }
      if (shape === "triUpForward") {
        leftTilt = -3;
        rightTilt = 3;
      }
      return (
        <>
          {bright === "bright" && (
            <>
              <path d={softTrianglePath(lx, cy, eyeW * 1.25, eyeH * 1.25, orient, leftTilt)} fill={color} opacity={0.25} />
              <path d={softTrianglePath(rx, cy, eyeW * 1.25, eyeH * 1.25, orient, rightTilt)} fill={color} opacity={0.25} />
            </>
          )}
          <Eye side="left" cx={lx} cy={cy}>
            <path d={softTrianglePath(lx, cy, eyeW, eyeH, orient, leftTilt)} fill={color} opacity={dim} />
          </Eye>
          <Eye side="right" cx={rx} cy={cy}>
            <path d={softTrianglePath(rx, cy, eyeW, eyeH, orient, rightTilt)} fill={color} opacity={dim} />
          </Eye>
        </>
      );
    }
  }
}
